import React, { useContext } from 'react';
import type { IHighlight } from 'react-pdf-highlighter';

import { PdfViewer } from './PdfViewer';
import { TabWrapper } from './TabWrapper';
import { StyledTabs } from './StyledTabs';
import { ChatBot } from './Chat/ChatBot';
import { SparkleIcon } from './icons';

import { useAntTheme } from '../hooks/useAntTheme';
import { hasChat, hasCustomChatLabel, hasDocumentAnimation } from '../helpers';
import { getUrlAccessToken } from '../utils/auth';
import { MULTIPLE_DOCS_DISPLAY } from '../constants';
import {
  Cuepoint,
  MediaProjectInstanceResponse,
  MessageOrigin,
  ProjectDocument,
} from '../types';
import { AppContext } from '../contexts/AppContext';

export type DocumentChatPanelProps = {
  publication: MediaProjectInstanceResponse;
  mainDocument: ProjectDocument | undefined;
  highlights: IHighlight[];
  cuepoints: Cuepoint[];
  pdfViewerRef: React.MutableRefObject<never>;
  organizationId: string | undefined;
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  chatHeight: number;
  onDragStart: (e: React.MouseEvent) => void;
  firstQuery: string | undefined;
  nextQuery: string | undefined;
  openChat: boolean;
  suggestions: string[] | undefined;
  setSuggestions: (s: string[] | undefined) => void;
  documentIds: string[];
  handleDisplaySource: (source: MessageOrigin) => void;
  cleanHighlightAndCuepoints: () => void;
  isCreatorMode?: boolean;
  afterDocument?: React.ReactNode;
  containerRef?: React.RefObject<HTMLDivElement>;
  containerStyle?: React.CSSProperties;
  /**
   * When true, the open chat panel (header + ChatBot body) and the drag-resize
   * handle are NOT rendered here. The collapsed input bar still renders when
   * chatOpen is false so it can act as the open-trigger. Used by the new
   * project page where, on chatOpen, the chat overlays the video in the left
   * panel instead of expanding inside this component.
   */
  chatInOverlay?: boolean;
};

export const DocumentChatPanel: React.FC<DocumentChatPanelProps> = React.memo(
  function DocumentChatPanel({
    publication,
    mainDocument,
    highlights,
    cuepoints,
    pdfViewerRef,
    organizationId,
    chatOpen,
    setChatOpen,
    chatHeight,
    onDragStart,
    firstQuery,
    nextQuery,
    openChat,
    suggestions,
    setSuggestions,
    documentIds,
    handleDisplaySource,
    cleanHighlightAndCuepoints,
    isCreatorMode,
    afterDocument,
    containerRef,
    containerStyle,
    chatInOverlay,
  }) {
    const listOfDocs = Object.values(publication?.documents ?? {});
    const showDocumentAnimation = hasDocumentAnimation(publication);
    const showChat = hasChat(publication);
    // When chat is rendered in an external overlay (e.g. left-panel video
    // overlay on the new project page), the chat-section here only acts as
    // the open-trigger: collapsed bar stays while chatOpen=false, and the
    // whole chat-section is hidden once chatOpen=true (the overlay takes over).
    const renderChatHere = !(chatInOverlay && chatOpen);

    const documentsTab = listOfDocs.map(
      (doc: ProjectDocument, index: number) => ({
        key: doc.id,
        label: doc.name,
        children: (
          <TabWrapper
            highlights={highlights}
            document={doc}
            index={index}
            pdfViewerRef={pdfViewerRef}
            cuepoints={cuepoints}
          />
        ),
      }),
    );

    const { theme } = useAntTheme();
    const { t, identifiers } = useContext(AppContext);
    // MED-6911 — specific experiences show a custom chat-trigger label.
    const chatTriggerLabel = hasCustomChatLabel(identifiers.projectId)
      ? t('askYourQuestionsHere')
      : t('chatWithAiAboutDocument');
    const searchParams =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams();
    const chatOnly = !showDocumentAnimation;

    return (
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          border: `1px solid ${theme.colorBorderSecondary}`,
          overflow: 'hidden',
          ...containerStyle,
        }}
      >
        {showDocumentAnimation && (
          <div
            style={{
              flex: chatOpen && renderChatHere ? 100 - chatHeight : 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            {documentsTab.length > 1 && MULTIPLE_DOCS_DISPLAY ? (
              <StyledTabs defaultActiveKey="1" items={documentsTab} />
            ) : mainDocument ? (
              <PdfViewer
                url={mainDocument.path}
                ref={pdfViewerRef}
                highlights={highlights}
                cuepoints={cuepoints}
                isPrimary={true}
                documentPagesCount={mainDocument.nb_of_pages}
                isCreatorMode={isCreatorMode}
              />
            ) : null}
          </div>
        )}
        {afterDocument}

        {!chatInOverlay && showDocumentAnimation && showChat && chatOpen && (
          <div className="chat-drag-handle" onMouseDown={onDragStart}>
            <span className="chat-drag-handle__pill" />
          </div>
        )}

        {!chatInOverlay && showChat && (
          <div
            className={`chat-section ${chatOpen || chatOnly ? 'chat-section--open' : 'chat-section--collapsed'}`}
            style={
              chatOpen || chatOnly
                ? { flex: chatOnly ? 1 : chatHeight, minHeight: 0 }
                : { flex: 'none' }
            }
          >
            {chatOpen || chatOnly ? (
              <>
                <div className="chat-section__header">
                  <div className="chat-section__header-left">
                    <SparkleIcon />
                    <span className="chat-section__header-title">
                      {t('askAboutDocument')}
                    </span>
                  </div>
                  {!chatOnly && (
                    <button
                      className="chat-section__close-btn"
                      onClick={() => setChatOpen(false)}
                      type="button"
                      aria-label="Close chat"
                    >
                      &#x2715;
                    </button>
                  )}
                </div>
                <div className="chat-section__body">
                  <ChatBot
                    publication={publication}
                    open={openChat || chatOpen || chatOnly}
                    mainDocumentId={mainDocument?.id}
                    publicationDocuments={publication?.documents}
                    firstQuery={firstQuery}
                    nextQuery={nextQuery}
                    documentIds={documentIds}
                    organizationId={organizationId ?? ''}
                    showSources={handleDisplaySource}
                    onBeforeQuery={cleanHighlightAndCuepoints}
                    suggestions={suggestions}
                    setSuggestions={setSuggestions}
                    previewAccessToken={getUrlAccessToken(searchParams)}
                  />
                </div>
              </>
            ) : (
              <div
                className="chat-collapsed-bar"
                role="button"
                tabIndex={0}
                onClick={() => setChatOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setChatOpen(true);
                }}
              >
                <SparkleIcon />
                <span className="chat-collapsed-bar__placeholder">
                  {t('askMeAnything')}
                </span>
                <button
                  className="chat-collapsed-bar__ask-btn"
                  type="button"
                >
                  {t('ask')}
                </button>
              </div>
            )}
          </div>
        )}

        {chatInOverlay && showChat && !chatOpen && (
          <button
            type="button"
            className="newmode-chat-trigger"
            onClick={() => setChatOpen(true)}
            aria-label={chatTriggerLabel}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{chatTriggerLabel}</span>
          </button>
        )}
      </div>
    );
  },
);
